// Rows for the Gameweek screen's title dropdown.
//
// The picker replaced the header's back arrow: the title itself is the control, and the dropdown
// it opens is the entry point into a past gameweek's results modal. This module is the pure row
// model — ordering, the Live row, and which chip icon (if any) a week carries — so those rules can
// be tested without a DOM.

import { getSortedFinalizedGameweeks } from './season'
import type { ChipId, GameweekResult } from './types'

export interface GameweekPickerRow {
  gameweek: number
  /** Points scored that week, or null for the live (current) gameweek, which shows "Live" instead —
   *  its points aren't final, so there is no number to stand behind yet. */
  points: number | null
  isLive: boolean
  /** Season chips played that week, in a stable order. Never includes the Banker: that's played
   *  every week, so it carries no information here — it belongs inside the modal, on the fixture it
   *  doubled. */
  chips: ChipId[]
  /** Whether tapping the row opens that gameweek's results. The live row never does — there are no
   *  results to open yet. */
  selectable: boolean
}

export interface GameweekPickerInput {
  currentGameweek: number
  history: GameweekResult[]
  doubleUpUsedGameweeks: number[]
  cleanSweepUsedGameweeks: number[]
  goalfestUsedGameweeks: number[]
}

/** Chips played in `gameweek`. Order is fixed (not usage order) so the icon set reads the same way
 *  every time — and matches the order the results modal lists them in, since the shared-element
 *  clone is that modal's heading. A Clean Sweep week that scored 0 still lists its chip: the chip
 *  is a record of what was played, not of what it paid out. */
function chipsForGameweek(gameweek: number, input: GameweekPickerInput): ChipId[] {
  const played: ChipId[] = []
  if (input.doubleUpUsedGameweeks.includes(gameweek)) played.push('doubleUp')
  if (input.cleanSweepUsedGameweeks.includes(gameweek)) played.push('cleanSweep')
  if (input.goalfestUsedGameweeks.includes(gameweek)) played.push('goalfest')
  return played
}

/** The dropdown's rows, newest first: the live gameweek on top, then every finalized gameweek in
 *  descending order. A gameweek that somehow appears in both (finalized but still "current") is
 *  listed once, as the live row. */
export function buildGameweekPickerRows(
  input: GameweekPickerInput
): GameweekPickerRow[] {
  const finalized = getSortedFinalizedGameweeks(input.history)
  const pointsByGameweek = new Map(
    input.history.map((entry) => [entry.gameweek, entry.points])
  )

  const liveRow: GameweekPickerRow = {
    gameweek: input.currentGameweek,
    points: null,
    isLive: true,
    chips: chipsForGameweek(input.currentGameweek, input),
    selectable: false,
  }

  const pastRows = finalized
    .filter((gameweek) => gameweek !== input.currentGameweek)
    .sort((a, b) => b - a)
    .map<GameweekPickerRow>((gameweek) => ({
      gameweek,
      points: pointsByGameweek.get(gameweek) ?? 0,
      isLive: false,
      chips: chipsForGameweek(gameweek, input),
      selectable: true,
    }))

  return [liveRow, ...pastRows]
}
